/**
 * Purpose:
 *   Wires the <vv-rv-carousel> custom element: a horizontally-scrollable,
 *   snap-aligned product carousel with prev/next arrows and a row of mobile
 *   pagination dots. One module drives THREE surfaces: the recently-viewed
 *   section (search + cart templates) and the cart-drawer recommendations
 *   carousel - all share the same markup contract (data-vv-rv-track,
 *   .vv-rv__slide cards, data-vv-rv-prev/next, [data-vv-rv-dots]).
 *
 * Public API:
 *   Registers the `vv-rv-carousel` custom element. No exported symbols.
 *   Markup contract:
 *     - scroll track: [data-vv-rv-track]
 *     - each card:    .vv-rv__slide
 *     - arrows:       [data-vv-rv-prev] / [data-vv-rv-next]
 *     - dots:         [data-vv-rv-dots] container + [data-vv-rv-dot="<i>"]
 *                     (rendered by snippets/vv-carousel-dots.liquid, prefix
 *                     'vv-rv'); absent -> dots controller is a graceful no-op.
 *
 * Upstream dependencies:
 *   - @theme/vv-carousel-dots (initCarouselDots) for the mobile dot row.
 *   - DOM/scroll APIs only otherwise. No other libraries.
 *
 * Downstream consumers:
 *   - sections/vv-recently-viewed.liquid (search + cart recently-viewed).
 *   - snippets/header-actions.liquid (cart-drawer recommendations carousel).
 *
 * Failure modes:
 *   - No-op when [data-vv-rv-track] is absent (returns before any wiring).
 *   - Dot controller no-ops when the dots container/buttons are absent or
 *     when there is a single reachable snap (see vv-carousel-dots.js).
 *   - All listeners and the dots click delegate share one AbortController and
 *     tear down together in disconnectedCallback.
 *
 * Performance:
 *   - Scroll/resize state updates are rAF-coalesced; the dot update is O(dots)
 *     synchronous layout reads per tick. No I/O, no timers, no network.
 */
import { initCarouselDots } from '@theme/vv-carousel-dots';

class VVRvCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-vv-rv-track]');
    this.prevButton = this.querySelector('[data-vv-rv-prev]');
    this.nextButton = this.querySelector('[data-vv-rv-next]');

    if (!this.track) return;

    this.abortController = new AbortController();
    this.scrollRaf = 0;
    const { signal } = this.abortController;

    this.prevButton?.addEventListener('click', () => this.scrollByCard(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.scrollByCard(1), { signal });

    this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
    window.addEventListener('resize', this.onResize, { signal });

    this.dotsCtl = initCarouselDots(this, {
      track: this.track,
      prefix: 'vv-rv',
      cardSelector: '.vv-rv__slide',
      signal,
    });
    this.dotsCtl.update();

    requestAnimationFrame(() => this.updateArrowState());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    cancelAnimationFrame(this.scrollRaf);
  }

  onScroll = () => {
    cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(() => this.updateArrowState());
  };

  onResize = () => {
    cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(() => this.updateArrowState());
  };

  updateArrowState() {
    if (!this.track) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= maxScroll - 2;

    this.prevButton?.toggleAttribute('disabled', atStart);
    this.nextButton?.toggleAttribute('disabled', atEnd);

    this.dotsCtl?.update();
  }

  scrollByCard(direction) {
    if (!this.track) return;

    const cards = this.track.querySelectorAll('.vv-rv__slide');
    if (!cards.length) return;

    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = cards[0].getBoundingClientRect().width;
    const step = cardWidth + gap;

    const current = this.track.scrollLeft;
    const target = direction > 0 ? current + step : current - step;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const clamped = Math.max(0, Math.min(max, target));

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.track.scrollTo({ left: clamped, behavior });
  }
}

if (!customElements.get('vv-rv-carousel')) {
  customElements.define('vv-rv-carousel', VVRvCarousel);
}
